import React, { useCallback, useEffect, useMemo, useState } from 'react';

type BaseImageProps = {
  src: string;
  quality?: number;
  placeholder?: string;
  isLazyLoading?: boolean;
  alt?: string;
};

type OptimizedImageProps = BaseImageProps & {
  hasOptimization: true;
  baseUrl: string;
  priority?: boolean;
};

type NonOptimizedImageProps = BaseImageProps & {
  hasOptimization?: false;
};

export type ImageProps =
  | (OptimizedImageProps & React.HTMLProps<HTMLImageElement>)
  | (NonOptimizedImageProps & React.HTMLProps<HTMLImageElement>);

function isOptimizedProps(props: ImageProps): props is OptimizedImageProps {
  return !!props.hasOptimization;
}

function getImageUrl(baseUrl: string, src: string): string {
  return `${baseUrl}/_next/image?url=${encodeURIComponent(src)}`;
}

export function ImageComponent(props: ImageProps): JSX.Element {
  const {
    src,
    quality = 75,
    placeholder,
    alt,
    isLazyLoading = false,
    hasOptimization = false,
    ...rest
  } = props;

  const priority = isOptimizedProps(props) ? props.priority ?? false : false;

  const [loaded, setLoaded] = useState(false);
  const [isNextJs, setIsNextJs] = useState(false);
  const [NextImage, setNextImage] = useState<React.ElementType | null>(null);

  const baseUrl = isOptimizedProps(props) ? props.baseUrl : '';

  const imgProps = useMemo<React.HTMLProps<HTMLImageElement>>(() => {
    if (isOptimizedProps(props)) {
      const {
        baseUrl: _b,
        hasOptimization: _h,
        ...optimizedRestProps
      } = props as OptimizedImageProps & React.HTMLProps<HTMLImageElement>;
      return optimizedRestProps;
    }
    return rest;
  }, [props, rest]);

  const optimizedSrc = isOptimizedProps(props)
    ? `${getImageUrl(baseUrl, src)}&w=3840&q=${quality}`
    : src;

  const isAbsoluteUrl = /^(https?:)\/\//i.test(src);

  const widths = useMemo(
    () => [640, 750, 828, 1080, 1200, 1920, 2048, 3840],
    []
  );
  const srcset = useMemo(() => {
    if (isOptimizedProps(props)) {
      return widths
        .map(
          (width) =>
            `${getImageUrl(baseUrl, src)}&w=${width}&q=${quality} ${width}w`
        )
        .join(', ');
    }
    return undefined;
  }, [baseUrl, src, quality, props, widths]);

  const currentSrc = loaded || !placeholder ? optimizedSrc : placeholder;

  const imageStyles = useMemo(() => {
    if (!loaded && placeholder) {
      return { filter: 'blur(5px)' };
    }
    return {};
  }, [loaded, placeholder]);

  // Detect if the app is using Next.js and dynamically import `next/image`
  useEffect(() => {
    // Dynamically import next/image and check for Next.js environment
    import('next/image')
      .then((mod) => {
        setNextImage(mod.default as unknown as React.ElementType);
        setIsNextJs(true);
      })
      .catch(() => setIsNextJs(false));
  }, [baseUrl]);

  // Fallback image render
  const renderFallbackImage = useCallback(
    () => (
      <>
        {isAbsoluteUrl ? (
          <img
            {...imgProps}
            alt={alt || ''}
            src={currentSrc}
            srcSet={srcset}
            sizes={hasOptimization ? '100vw' : undefined}
            onLoad={() => setLoaded(true)}
            loading={isLazyLoading ? 'lazy' : 'eager'}
            style={imageStyles}
          />
        ) : (
          <img {...imgProps} alt={alt || ''} src={src} />
        )}
      </>
    ),
    [
      imgProps,
      alt,
      currentSrc,
      srcset,
      hasOptimization,
      isLazyLoading,
      imageStyles,
      isAbsoluteUrl,
      src,
    ]
  );

  // Render next/image or fallback image
  return (
    <>
      {isNextJs && NextImage && baseUrl !== '' ? (
        <NextImage
          {...imgProps}
          src={src}
          alt={alt || ''}
          layout="responsive"
          width={1920} // Default or passed in width
          height={1080} // Default or passed in height
          quality={quality}
          placeholder={placeholder ? 'blur' : undefined}
          blurDataURL={placeholder}
          loading={isLazyLoading ? 'lazy' : 'eager'}
          priority={priority}
        />
      ) : (
        renderFallbackImage()
      )}
    </>
  );
}
